import moment, { unitOfTime } from 'moment';
import dayjs, { Dayjs } from 'dayjs';
import { IDataItem } from '../repertoire/types/IDataItem';
import IRepertoireComponentDataItem from '../redux/types/IRepertoireComponentDataItem';
import { IMembersPortalField } from '../membersportal/types/IMembersPortalField';
import { IPageAnchorTarget } from '../membersportal/types/IPageAnchorTarget';
import { IRepertoireField } from '../repertoire/types/IRepertoireField';
import { PAGE_ANCHOR_TARGET_KEY } from '../membersportal/Consts';
import { MomentInput } from 'moment/moment';

export function isNullOrUndefined(value: any) {
    return value === null || value === undefined;
}

export function isValidNumber(value: string): boolean {
    return /^-?\d+(\.\d+)?$|^$/.test(value);
}

export function areAllObjectsUnique(arr: any[], ignoreProps: string[]): { isInvalid: boolean; index: number | null } {
    const seen = new Set();
    for (let index = 0; index < arr.length; index++) {
        const uniqueKey = JSON.stringify(
            Object.keys(arr[index])
                .filter(key => !ignoreProps.includes(key))
                .sort()
                .reduce((acc, key) => {
                    acc[key] = arr[index][key];
                    return acc;
                }, {})
        );
        if (seen.has(uniqueKey))
            return { isInvalid: true, index };

        seen.add(uniqueKey);
    }
    return { isInvalid: false, index: null };
}

export function timeToMinutes(time?: string): number {
    if (!time) return 0;

    const timePart = time.includes("T")
        ? time.split("T")[1]?.slice(0, 5)
        : time;

    if (!timePart) return 0;

    const [hours, minutes] = timePart.split(":").map(Number);

    if (isNaN(hours) || isNaN(minutes)) return 0;
    return hours * 60 + minutes;
}

export function checkUniqueTimeRange(arr: any[]): { isInvalid: boolean; index: number | null } {
    for (let i = 0; i < arr.length - 1; i++) {
        const a = arr[i], b = arr[i + 1];
        if (a?.effectiveDateRange?.value === b?.effectiveDateRange?.value) {
            const dimensionsMatch = ["musicSource", "use", "usageDimensionOne", "usageDimensionTwo", "usageDimensionThree", "usageDimensionFour", "usageDimensionFive"]
                .every(key => a?.[key]?.value === b?.[key]?.value);
            if (dimensionsMatch) {
                const checkMin = timeToMinutes(b.fromTime?.value);
                const startMin = timeToMinutes(a.fromTime?.value);
                const endMin = timeToMinutes(a.toTime?.value);
                if (checkMin >= startMin && checkMin <= endMin) {
                    return { isInvalid: true, index: i };
                }
            }
        }
    }
    return { isInvalid: false, index: null };
}

export function checkFromTimeValidationWithDimensions(
    weights: any[] = [],
    dimensions: any[] = [],
    checkMissingOnly: boolean = false): { isInvalid: boolean; index: number | null } {

    for (let index = 0; index < weights.length; index++) {
        const weight = weights[index];
        const dim = dimensions.find(d => d.poolDimensionsAndDatesID === weight.dimensionsAndDatesID);
        if (!dim?.time)
           continue;

        const from = weight?.fromTime?.value;
        const to = weight?.toTime?.value;
        
        if (!from || !to) {
            return { isInvalid: true, index };
        }
        
        if (checkMissingOnly) {
            continue;
        }

        const startMin = timeToMinutes(from);
        const endMin = timeToMinutes(to);
        if (startMin >= endMin)
            return { isInvalid: true, index };
    }

    return { isInvalid: false, index: null };
}

export function checkOverLappingDates(evppDates: any[]): boolean {
    const dateRanges = evppDates.map(e => ({ effectiveFrom: e.effectiveFrom?.value, effectiveTo: e.effectiveTo?.value }));
    return dateRanges.some((lineItem, index) => {
        const next = dateRanges[index + 1];
        return next && isDateRangesOverLapping(lineItem, next);
    });
}


export function checkDateValidation(evppDates: any[]): boolean {
    const dateRanges = evppDates.map(e => ({
        effectiveFrom: e.effectiveFrom?.value,
        effectiveTo: e.effectiveTo?.value
    }));

    return dateRanges.some(({ effectiveFrom, effectiveTo }) =>
        effectiveFrom && effectiveTo && effectiveFrom > effectiveTo
    );
}


export const isDateRangesOverLapping = (rangeA, rangeB) => {
    const isEffectiveFromDateLineA = !!rangeA.effectiveFrom;
    const isEffectiveToDateLineA = !!rangeA.effectiveTo;
    const isEffectiveFromDateLineB = rangeB && !!rangeB.effectiveFrom;
    const isEffectiveToDateLineB = rangeB && !!rangeB.effectiveTo;
    if (isEffectiveFromDateLineA) {
        const momentEffectiveFromDateA = moment(rangeA.effectiveFrom);
        if (isEffectiveToDateLineA) {
            const momentEffectiveToDateA = moment(rangeA.effectiveTo);
            if (isEffectiveFromDateLineB) {
                const dateBFrom = rangeB.effectiveFrom;
                if (isEffectiveToDateLineB) {
                    const dateBTo = rangeB.effectiveTo;
                    const rangeIsBeforeNextRange = momentEffectiveFromDateA.isBefore(dateBFrom) && momentEffectiveToDateA.isBefore(dateBFrom);
                    const rangeIsAfterNextRange = momentEffectiveFromDateA.isAfter(dateBTo) && momentEffectiveToDateA.isAfter(dateBTo);
                    const rangeEncompassNextRange = momentEffectiveFromDateA.isBefore(dateBFrom) && momentEffectiveToDateA.isAfter(dateBTo);
                    const rangeIsEncompassByNextRange = momentEffectiveFromDateA.isAfter(dateBFrom) && momentEffectiveToDateA.isBefore(dateBTo);
                    const rangeOverLappsStartOrEndDay = momentEffectiveFromDateA.isSame(dateBFrom, 'day') || momentEffectiveToDateA.isSame(dateBFrom, 'day') || momentEffectiveFromDateA.isSame(dateBTo, 'day') || momentEffectiveToDateA.isSame(dateBTo, 'day')
                    return !((rangeIsBeforeNextRange || rangeIsAfterNextRange) && !rangeEncompassNextRange && !rangeIsEncompassByNextRange && !rangeOverLappsStartOrEndDay);
                }
                const rangeOverLappsStartOrEndDay = momentEffectiveFromDateA.isSame(dateBFrom, 'day') || momentEffectiveToDateA.isSame(dateBFrom, 'day');
                const rangeIsBeforeNextRange = momentEffectiveFromDateA.isBefore(dateBFrom) && momentEffectiveToDateA.isBefore(dateBFrom);
                return !rangeIsBeforeNextRange || rangeOverLappsStartOrEndDay;
            }
            return false;
        }
        if (isEffectiveFromDateLineB) {
            const dateBFrom = rangeB.effectiveFrom;
            if (isEffectiveToDateLineB) {
                const dateBTo = rangeB.effectiveTo;
                const rangeOverLappsStartOrEndDay = momentEffectiveFromDateA.isSame(dateBFrom, 'day') || momentEffectiveFromDateA.isSame(dateBTo, 'day');
                const rangeAIsAfterRangeB = momentEffectiveFromDateA.isAfter(dateBTo) && momentEffectiveFromDateA.isAfter(dateBFrom);
                return !rangeAIsAfterRangeB || rangeOverLappsStartOrEndDay;
            }
            return true;
        }
        return false
    }
    return false;
}

export function isInvalidDateTimeRange(startDateTime?: string, endDateTime?: string
): boolean {
    if (!startDateTime || !endDateTime) {
        return true;
    }

    const start = new Date(startDateTime);
    const end = new Date(endDateTime);

    return (
        Number.isNaN(start.getTime()) ||
        Number.isNaN(end.getTime()) ||
        start >= end
    );
}

export function convertSecondsToTimeStamp(value: number) {
    
    const date = new Date(0);
    date.setSeconds(value);
    const timeString = date.toISOString().substring(11, 19);

    // if seconds provided are more than one day
    if (value > 86399) {
        const days = ((value / 60) / 60) / 24;
        const floorDays = Math.floor(days) * 24;
        const splittedArray = timeString.split(':');
        const newHoursCount = (Number(splittedArray[0]) + floorDays).toString();
        return `${newHoursCount}:${splittedArray[1]}:${splittedArray[2]}`;
    }

    return timeString;
}

export function convertStringToList(item: IDataItem): string[] {
    return item?.value === '' ? [] : item?.value.split(',');
}

export function areArraysOverlapping(arr1: string[], arr2: string[]): boolean {
    return arr1.some(item => arr2.includes(item));
}

export function getMandatoryFlag(componentData: IRepertoireComponentDataItem | undefined, fieldName: string): boolean {
    return componentData?.fields?.some(
        field => field.name === fieldName && field.isMandatory === true
    ) || false;
}

export function applyFieldOverrides(
    fields: IRepertoireField[] | undefined,
    overrides?: Partial<IRepertoireField>[]
): IRepertoireField[] {
    if (!fields) return [];

    return fields.map(field => {
        const override = overrides?.find(o => o.name === field.name);
        return override ? { ...field, ...override } : field;
    });
}

export function isMandatoryField(field: IRepertoireField | IMembersPortalField | undefined): boolean {
    return field?.isMandatory === true;
}

export function hasValue<T>(object: any, value: T): boolean {
    if (Array.isArray(object)) {
        return object.some(v => v === value);
    }
    if (typeof object === "object" && object !== null) {
        return Object.values(object).some(v => v === value);
    }
    return object === value;
}

export function hasAnyValidValue(...values: Array<string | null | undefined>): boolean {
    return values.some(
        value => !!value && !/^\s*0+\s*$/.test(value)
    );
}

export function buildDisabledDate(allowFuture: boolean, pastCutoff: number | null) {
    const today = dayjs().startOf('day');
    return (current?: dayjs.Dayjs) => {
        if (!current) return false;

        if (allowFuture === false && current.isAfter(today, 'day')) return true;

        if (pastCutoff != null) {
            const earliest = today.subtract(pastCutoff, 'day');
            if (current.isBefore(earliest, 'day')) return true;
        }

        return false;
    };
}

export function setPageAnchorTarget(target: IPageAnchorTarget): void {
    try {
        sessionStorage.setItem(
            PAGE_ANCHOR_TARGET_KEY,
            JSON.stringify(target)
        );
    } catch {
    }
}

export function handlePageAnchorNavigation(retryCount: number = 5): void {
    const raw = sessionStorage.getItem(PAGE_ANCHOR_TARGET_KEY);
    if (!raw) return;

    let target: IPageAnchorTarget | null = null;

    try {
        target = JSON.parse(raw);
    } catch {
        sessionStorage.removeItem(PAGE_ANCHOR_TARGET_KEY);
        return;
    }

    if (!target) return;

    if (window.location.pathname !== target.path) {
        if (retryCount > 0) {
            setTimeout(() => handlePageAnchorNavigation(retryCount - 1), 100);
        }
        return;
    }

    const element = document.querySelector(
        `[data-page-anchor="${target.anchor}"]`
    ) as HTMLElement | null;

    if (!element) {
        if (retryCount > 0) {
            setTimeout(() => handlePageAnchorNavigation(retryCount - 1), 100);
        }
        return;
    }

    const yOffset = -80;
    const y =
        element.getBoundingClientRect().top +
        window.pageYOffset +
        yOffset;

    window.scrollTo({
        top: y,
        behavior: "smooth",
    });

    sessionStorage.removeItem(PAGE_ANCHOR_TARGET_KEY);
}

export function formatCurrencyWithCommas(
    value: any,
    includeSymbol: boolean = true,
    decimalPlaces: number = 2
): string {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    const parsed =
        typeof value === 'number'
            ? value
            : Number(String(value).replace(/,/g, ''));

    if (Number.isNaN(parsed)) {
        return String(value);
    }

    const formatted = parsed.toLocaleString('en-US', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces
    });

    return includeSymbol ? `$${formatted}` : formatted;
}

export function getDatePart(date?: string): string | undefined {
    if (!date) {
        return undefined;
    }

    return date.includes("T") ? date.split("T")[0] : date;
}

export function formatDateToDisplay(date?: string): string {
    if (!date) return "";

    const dateOnly = getDatePart(date);

    return dateOnly ? dateOnly.split("-").reverse().join("/") : "";
}

export function adjustProgramDates(programStartDate: string, programEndDate: string, programStartUsageDate: string, programEndUsageDate?: string): { startDate: Date, endDate: Date } | null  {

    const startDatePart = getDatePart(programStartUsageDate);
    const endDatePart = getDatePart(
        programEndUsageDate ?? programStartUsageDate
    );

    if (!programStartDate || !programEndDate || !startDatePart || !endDatePart) {
        return null;
    }

    const mapDateToTime = (programDate: string): [number, number] | null => {
        const [hour, minute] = programDate.length <= 5 ? programDate.split(':').map(Number) : programDate.split(/[-:TZ]/).slice(3, 5).map(Number);

        if (Number.isNaN(hour) || Number.isNaN(minute)) {
            return null;
        }
        return [hour, minute];
    };

    const startTime = mapDateToTime(programStartDate);
    const endTime = mapDateToTime(programEndDate);

    if (!startTime || !endTime) {
        return null;
    }

    const [startYear, startMonth, startDay] = startDatePart.split("-").map(Number);

    const [endYear, endMonth, endDay] = endDatePart.split("-").map(Number);

    const [startHour, startMinute] = startTime;
    const [endHour, endMinute] = endTime;

    const startDate = new Date(Date.UTC(startYear, startMonth - 1, startDay, startHour, startMinute));

    const endDate = new Date(Date.UTC(endYear, endMonth - 1, endDay, endHour, endMinute));

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return null;
    }

    return { startDate, endDate };
}

export function isDateWithinInterval(startDate: MomentInput, endDate: MomentInput, interval: number, unit: unitOfTime.DurationConstructor): boolean {
    const start = moment(startDate);
    const end = moment(endDate);

    if (!start.isValid() || !end.isValid() || interval < 0) {
        return false;
    }

    if (end.isBefore(start, "day")) {
        return false;
    }

    return !end.isAfter(start.clone().add(interval, unit), "day");
}